import { AppNS } from 'app';
import { checkErrors, getErrorMessage } from 'app/ui_v2/form-utils';
import { ReactNode } from 'react';
import { useController } from 'react-hook-form';

import { Autocomplete, CircularProgress, FormControl, FormHelperText, TextField } from '@mui/material';

import LabelTooltip from '../label-tooltip/LabelTooltip';

export interface BasicAutocompleteProps {
  collection: { id: string; name: string }[];
  label: string;
  control: any;
  inputName: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  isOptional?: boolean;
  tooltipText?: string | ReactNode;
  isLoading?: boolean;
  placeholder?: string;
  onInputChange?: (value: string) => void;
  onValueChange?: (value: AppNS.Params) => void;
}

const getCollectionItemById = (collection: { id: string; name: string }[], id: string) => {
  return collection.find((x) => x.id === id);
};

export default function BasicAutocomplete(props: BasicAutocompleteProps) {
  const {
    collection,
    control,
    label,
    inputName,
    required,
    disabled,
    onInputChange,
    onValueChange,
    helperText,
    isLoading = false,
    tooltipText,
    placeholder,
  } = props;

  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({
    name: inputName,
    control,
    defaultValue: null,
  });

  const isErrors = checkErrors(inputName, errors);

  return (
    <>
      <LabelTooltip
        tooltipText={tooltipText}
        label={label}
        disabled={disabled}
        inputName={inputName}
        required={required}
        error={isErrors}
      />
      <FormControl error={isErrors}>
        <Autocomplete
          value={value}
          disabled={disabled}
          options={collection}
          autoComplete
          noOptionsText="Type to search"
          // input={<Input ref={myRef} />}
          isOptionEqualToValue={(option, value) => {
            // console.log(option, value);
            return option.id === value;
          }}
          getOptionLabel={(option) =>
            typeof option === 'string' ? getCollectionItemById(collection, option)?.name || '' : option.name
          }
          onChange={(e, newValue) => {
            onChange(collection.length ? newValue?.id || null : null);
            onValueChange && onValueChange(newValue);
          }}
          onInputChange={(event, newInputValue) => {
            if (!newInputValue) {
              onChange(null);
            }
            onInputChange && onInputChange(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              error={isErrors}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const name =
              typeof option === 'string' ? getCollectionItemById(collection, option)?.name || '' : option.name;
            return (
              <li {...props} key={option.id}>
                <span>{name}</span>
              </li>
            );
          }}
        />
        {(isErrors || helperText) && (
          <FormHelperText>{getErrorMessage(inputName, errors) || helperText}</FormHelperText>
        )}
      </FormControl>
    </>
  );
}
